import React, { useEffect, useState } from 'react';
import { baseUrl } from '../../shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtHours = (v) => (Math.round((Number(v) || 0) * 100) / 100).toFixed(2);

const SELECT_ALL = '__all';

function AdminTimesheetReport() {
  const now = new Date();
  const [startDate, setStartDate] = useState(fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [endDate, setEndDate] = useState(fmtDate(now));
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [rows, setRows] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = {
    'Content-Type': 'application/json',
    'username': localStorage.getItem('username'),
    'bToken': localStorage.getItem('bToken'),
  };

  useEffect(() => {
    fetch(baseUrl + 'reporting/adminTimesheet/users', { method: 'GET', headers: authHeaders })
      .then((res) => res.json())
      .then((json) => {
        if (Array.isArray(json)) {
          setUsers(json);
          setSelectedUsers(json.map((u) => u.username));
        } else {
          setError(json?.message || 'Could not load users');
        }
      })
      .catch((err) => {
        console.log(err.message);
        setError('Could not load users');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUserChange = (e) => {
    const value = e.target.value;
    if (value.includes(SELECT_ALL)) {
      setSelectedUsers(selectedUsers.length === users.length ? [] : users.map((u) => u.username));
    } else {
      setSelectedUsers(value);
    }
  };

  const paramsValid = startDate && endDate && startDate <= endDate && selectedUsers.length > 0;
  const reportBody = JSON.stringify({ startDate, endDate, usernames: selectedUsers });

  const runReport = () => {
    setLoading(true);
    setError('');
    fetch(baseUrl + 'reporting/adminTimesheet/summary', { method: 'POST', headers: authHeaders, body: reportBody })
      .then((res) => res.json())
      .then((json) => {
        if (Array.isArray(json)) {
          setRows(json);
          setExpandedUsers({});
        } else {
          setError(json?.message || 'Could not run report');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err.message);
        setError('Could not run report');
        setLoading(false);
      });
  };

  const exportReport = async () => {
    setExporting(true);
    setError('');
    try {
      const response = await fetch(baseUrl + 'reporting/adminTimesheet/export', { method: 'POST', headers: authHeaders, body: reportBody });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const linkurl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = linkurl;
      link.download = `AdminTimesheetReport_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(linkurl);
    } catch (err) {
      console.log(err.message);
      setError('Could not export report');
    }
    setExporting(false);
  };

  const numCell = { textAlign: 'right' };
  const numValue = (v, opts = {}) => (
    <span className="aims-mono" style={{ color: opts.warn && Number(v) > 0 ? 'var(--warn)' : undefined, fontWeight: opts.bold ? 700 : undefined }}>
      {fmtHours(v)}
    </span>
  );
  const MobileStat = ({ label, value, warn }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Box component="span" sx={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{label}</Box>
      {numValue(value, { warn })}
    </Box>
  );

  const toggleUser = (username) => setExpandedUsers((prev) => ({ ...prev, [username]: !prev[username] }));

  const TimesheetBreakdown = ({ timesheets }) => (
    (timesheets || []).length < 1 ? (
      <Typography variant="caption" sx={{ color: 'var(--muted)' }}>No timesheets in this period.</Typography>
    ) : (
      <Box>
        {timesheets.map((t, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, py: 0.375, borderTop: i > 0 ? '1px dashed var(--border)' : 'none' }}>
            <Box component="span" className="aims-mono" sx={{ fontSize: '0.8rem', flexShrink: 0 }}>{t.workPerformedOn}</Box>
            <Box component="span" className="aims-mono" sx={{ fontSize: '0.8rem', flex: 1, minWidth: 0, overflowWrap: 'anywhere', color: 'var(--muted)' }}>{t.assetNo}</Box>
            <Box component="span" className="aims-mono" sx={{ fontSize: '0.8rem', flexShrink: 0 }}>{fmtHours(t.hours)}</Box>
          </Box>
        ))}
      </Box>
    )
  );

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Admin Timesheet Report</Typography>
      <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mb: 2 }}>
        Timesheet hours by user for a period. Start and end dates are inclusive.
      </Typography>

      <Box sx={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow)', p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Start date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: { xs: '1 1 40%', sm: '0 0 auto' } }}
        />
        <TextField
          label="End date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: { xs: '1 1 40%', sm: '0 0 auto' } }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, flex: 1 }}>
          <InputLabel id="admin-report-users-label">Users</InputLabel>
          <Select
            labelId="admin-report-users-label"
            multiple
            value={selectedUsers}
            onChange={handleUserChange}
            input={<OutlinedInput label="Users" />}
            renderValue={(selected) => selected.length === users.length ? `All users (${users.length})` : `${selected.length} of ${users.length} users`}
          >
            <MenuItem value={SELECT_ALL}>
              <Checkbox checked={users.length > 0 && selectedUsers.length === users.length} indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length} />
              <ListItemText primary="Select all" />
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.username} value={u.username}>
                <Checkbox checked={selectedUsers.includes(u.username)} />
                <ListItemText primary={u.name || u.username} secondary={u.username} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" disabled={!paramsValid || loading} onClick={runReport} sx={{ flex: { xs: 1, sm: '0 0 auto' } }}>
          {loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Run Report'}
        </Button>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={!paramsValid || !rows || rows.length < 1 || exporting} onClick={exportReport} sx={{ flex: { xs: 1, sm: '0 0 auto' } }}>
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </Box>

      {error ? (
        <Typography sx={{ color: 'var(--fail)', mb: 2 }}>{error}</Typography>
      ) : null}

      {rows ? (
        <TableContainer component={Paper} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow)', display: { xs: 'none', md: 'block' } }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, background: 'var(--surface-2)' } }}>
                <TableCell>User</TableCell>
                <TableCell sx={numCell}>Assets</TableCell>
                <TableCell sx={numCell}>Workshop</TableCell>
                <TableCell sx={numCell}>Onsite</TableCell>
                <TableCell sx={numCell}>Travel</TableCell>
                <TableCell sx={numCell}>WIP Hours</TableCell>
                <TableCell sx={numCell}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <React.Fragment key={r.username}>
                  <TableRow hover onClick={() => toggleUser(r.username)} sx={{ cursor: 'pointer', opacity: Number(r.totalHours) === 0 ? 0.55 : 1 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {expandedUsers[r.username]
                          ? <ExpandLessIcon fontSize="small" sx={{ color: 'var(--muted)' }} />
                          : <ExpandMoreIcon fontSize="small" sx={{ color: 'var(--muted)' }} />}
                        <Box>
                          <Box component="span" sx={{ display: 'block' }}>{r.name}</Box>
                          <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', color: 'var(--muted)' }}>{r.username}</Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={numCell}>{numValue(r.otherHours)}</TableCell>
                    <TableCell sx={numCell}>{numValue(r.workshopHours)}</TableCell>
                    <TableCell sx={numCell}>{numValue(r.onsiteHours)}</TableCell>
                    <TableCell sx={numCell}>{numValue(r.travelHours)}</TableCell>
                    <TableCell sx={numCell}>{numValue(r.wipHours, { warn: true })}</TableCell>
                    <TableCell sx={numCell}>{numValue(r.totalHours, { bold: true })}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedUsers[r.username] ? undefined : 'none' }}>
                      <Collapse in={!!expandedUsers[r.username]} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 3, py: 1.5, background: 'var(--surface-2)', maxWidth: 480 }}>
                          <TimesheetBreakdown timesheets={r.timesheets} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {rows.length < 1 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'var(--muted)', py: 3 }}>
                    No users selected.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography sx={{ color: 'var(--muted)' }}>Pick a date range and users, then run the report.</Typography>
      )}

      {rows ? (
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {rows.length < 1 ? (
            <Typography sx={{ color: 'var(--muted)' }}>No users selected.</Typography>
          ) : (
            <>
              {rows.map((r) => (
                <Box key={r.username} onClick={() => toggleUser(r.username)} sx={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow)', p: 1.5, mb: 1, cursor: 'pointer', opacity: Number(r.totalHours) === 0 ? 0.55 : 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box component="span" sx={{ display: 'block', fontWeight: 600, overflowWrap: 'anywhere' }}>{r.name}</Box>
                      <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', color: 'var(--muted)' }}>{r.username}</Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      {numValue(r.totalHours, { bold: true })}
                      {expandedUsers[r.username]
                        ? <ExpandLessIcon fontSize="small" sx={{ color: 'var(--muted)' }} />
                        : <ExpandMoreIcon fontSize="small" sx={{ color: 'var(--muted)' }} />}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 0.5 }}>
                    <MobileStat label="Assets" value={r.otherHours} />
                    <MobileStat label="Workshop" value={r.workshopHours} />
                    <MobileStat label="Onsite" value={r.onsiteHours} />
                    <MobileStat label="Travel" value={r.travelHours} />
                    <MobileStat label="WIP" value={r.wipHours} warn />
                  </Box>
                  <Collapse in={!!expandedUsers[r.username]} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid var(--border)' }}>
                      <TimesheetBreakdown timesheets={r.timesheets} />
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </>
          )}
        </Box>
      ) : null}

      {rows && rows.length > 0 ? (
        <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mt: 1 }}>
          Assets, Workshop, Onsite and Travel add up to Total. WIP hours are already counted in these numbers; the WIP column just shows how many of them are on timesheets not completed yet.
        </Typography>
      ) : null}
    </Box>
  );
}

export default AdminTimesheetReport;
